import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Download, Eye, Loader2, Calendar, User, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { safeFormat } from '@/lib/safeDate';
import { toast } from "sonner";
import PdfViewer from "@/components/common/PdfViewer";

const statusColors = {
  pending: "bg-amber-100 text-amber-700 border-amber-300",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-rose-100 text-rose-700 border-rose-300"
};

export default function DocumentsTab({ leadId }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [leadData, setLeadData] = useState(null);
  const [showFormData, setShowFormData] = useState(false);

  useEffect(() => {
    loadDocuments();
    loadLeadData();
  }, [leadId]);

  async function loadLeadData() {
    if (!leadId) return;

    try {
      const { data, error } = await supabase
        .from('leads')
        .select('raw_data, first_name, last_name, primary_email, primary_phone, origem, created_at')
        .eq('id', leadId)
        .single();

      if (error) throw error;
      setLeadData(data);
    } catch (error) {
      console.error('Error loading lead data:', error);
    }
  }

  async function loadDocuments() {
    if (!leadId) return;

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('owner_type', 'lead')
        .eq('owner_id', leadId)
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDownload(doc) {
    try {
      const { data, error } = await supabase.storage
        .from('eligibility-pdfs')
        .download(doc.storage_path.replace('eligibility-pdfs/', ''));

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Download iniciado');
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Erro ao baixar documento');
    }
  }

  async function handleView(doc) {
    try {
      const { data, error } = await supabase.storage
        .from('eligibility-pdfs')
        .download(doc.storage_path.replace('eligibility-pdfs/', ''));

      if (error) throw error;

      const fileBlob = new Blob([data], { type: 'application/pdf' });
      setPdfUrl(fileBlob);
      setViewingDoc(doc);
    } catch (error) {
      console.error('Error viewing:', error);
      toast.error('Erro ao visualizar documento');
    }
  }

  function closeViewer() {
    setViewingDoc(null);
    setPdfUrl(null);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Verifica se há dados do formulário no raw_data
  const hasFormData = leadData?.raw_data?.field_data && Array.isArray(leadData.raw_data.field_data);

  if (documents.length === 0 && !hasFormData) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <FileText className="w-12 h-12 text-slate-300 mb-4" />
          <p className="text-slate-500 text-center">
            Nenhum documento encontrado
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Mostrar dados do formulário se disponível */}
        {hasFormData && (
          <Card className="hover:shadow-md transition-shadow border-blue-200 bg-blue-50/30">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-slate-900">
                        Dados do Formulário de Lead
                      </h3>
                      <Badge className="bg-blue-100 text-blue-700 border-blue-300 border">
                        Facebook Lead Form
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-2">
                      Origem: {leadData.origem || 'Facebook Lead Ads'}
                    </p>
                    
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {safeFormat(new Date(leadData.created_at), "dd/MM/yyyy 'às' HH:mm")}
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {leadData.first_name} {leadData.last_name}
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setShowFormData(!showFormData)}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {showFormData ? 'Ocultar' : 'Ver Dados'}
                </Button>
              </div>

              {/* Mostrar dados expandidos */}
              {showFormData && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {leadData.raw_data.field_data.map((field, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-slate-200">
                        <div className="text-xs font-medium text-slate-500 mb-1">
                          {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="text-sm text-slate-900">
                          {Array.isArray(field.values) ? field.values.join(', ') : field.values}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documentos regulares */}
        {documents.map((doc) => (
        <Card key={doc.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {doc.name}
                    </h3>
                    <Badge className={`${statusColors[doc.status]} border`}>
                      {doc.status}
                    </Badge>
                  </div>
                  
                  {doc.category && (
                    <p className="text-sm text-slate-600 mb-2">{doc.category}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                    {doc.uploaded_at && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {safeFormat(new Date(doc.uploaded_at), "dd/MM/yyyy 'às' HH:mm")}
                      </div>
                    )}
                    {doc.uploaded_via && (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Via {doc.uploaded_via}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-2 flex-shrink-0">
                <Button
                  onClick={() => handleView(doc)}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Visualizar
                </Button>
                <Button
                  onClick={() => handleDownload(doc)}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Baixar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        ))}
      </div>

      <Dialog open={!!viewingDoc} onOpenChange={closeViewer}>
        <DialogContent className="max-w-6xl h-[90vh]">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.name}</DialogTitle>
            <DialogDescription className="sr-only">Visualização de PDF</DialogDescription>
          </DialogHeader>
          <div className="w-full flex-1 overflow-hidden">
            {pdfUrl && <PdfViewer fileBlob={pdfUrl} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
